import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

export const JOB_MODES = ["Remote", "Hybrid", "On-site"];
export const JOB_TYPES = ["All", "Full-time", "Part-time", "Contract", "Internship"];

export const jobsData = [];

const WP_API_BASE = "https://travel.rethinkways.com/wp-json/wp/v2";
const WP_JOB_CATEGORY_IDS = {
  "Customer Service": 885,
  "Hospitality & Tourism": 1573,
  "Human Resource": 886,
  Legal: 888,
  Marketing: 883,
  Operations: 753,
  "Product Development": 884,
  Technology: 887,
  Administration: 889,
  "Assistant Chief Engineer": 1331,
  Sales: 749,
  Finance: 881,
};
const WP_JOB_CATEGORY_ID_LIST = Object.values(WP_JOB_CATEGORY_IDS).join(",");
const WP_JOB_CATEGORY_BY_ID = Object.fromEntries(
  Object.entries(WP_JOB_CATEGORY_IDS).map(([label, id]) => [id, label])
);
const toJobCategorySlug = (label = "") =>
  label
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
const KNOWN_JOB_CITIES = [
  "Ahmedabad",
  "Bangalore",
  "Bengaluru",
  "Chandigarh",
  "Chennai",
  "Coimbatore",
  "Delhi",
  "Goa",
  "Gurugram",
  "Gurgaon",
  "Hyderabad",
  "Jaipur",
  "Kochi",
  "Kolkata",
  "Mumbai",
  "Noida",
  "Pune",
  "Thiruvananthapuram",
];
const WP_JOBS_FIRST_PAGE_SIZE = 24;
const WP_JOBS_BACKGROUND_PAGE_SIZE = 100;
const WP_JOBS_CACHE_TTL_MS = 5 * 60 * 1000;
const WP_JOBS_FIRST_LOAD_TIMEOUT_MS = 8000;
const liveJobsCache = {
  data: null,
  timestamp: 0,
};

function buildJobsUrl({ page = 1, perPage = WP_JOBS_FIRST_PAGE_SIZE, offset } = {}) {
  const params = new URLSearchParams({
    per_page: String(perPage),
    categories: WP_JOB_CATEGORY_ID_LIST,
    _embed: "1",
    orderby: "date",
    order: "desc",
  });

  if (typeof offset === "number") {
    params.set("offset", String(offset));
  } else {
    params.set("page", String(page));
  }

  return `${WP_API_BASE}/posts?${params.toString()}`;
}

async function fetchJobsPage(options, signal) {
  const response = await fetch(buildJobsUrl(options), { signal });
  if (!response.ok) throw new Error(`WordPress returned ${response.status}`);

  return {
    posts: await response.json(),
    total: Number(response.headers.get("X-WP-Total") || 0),
  };
}

function normalizeJobs(posts = []) {
  return posts
    .map(normalizeWpJob)
    .filter((job) => job.title && job.company);
}

function mergeJobs(existingJobs, newJobs) {
  const byId = new Map();
  [...existingJobs, ...newJobs].forEach((job) => {
    if (job?.id) byId.set(job.id, job);
  });

  return Array.from(byId.values()).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
}

function cacheLiveJobs(jobs) {
  liveJobsCache.data = jobs;
  liveJobsCache.timestamp = Date.now();
}

function normalizeJobLocation(value = "") {
  const cleaned = String(value)
    .replace(/\s+/g, " ")
    .replace(/\b\d{6}\b/g, "")
    .trim()
    .replace(/\s*[-–—]\s*$/g, "");
  if (!cleaned) return "";
  if (/\bremote\b/i.test(cleaned)) return "Remote";

  const lower = cleaned.toLowerCase();
  const matchedCity = KNOWN_JOB_CITIES
    .map((city) => ({ city, index: lower.search(new RegExp(`\\b${city.toLowerCase()}\\b`, "i")) }))
    .filter(({ index }) => index >= 0)
    .sort((a, b) => a.index - b.index)[0]?.city;
  if (matchedCity) return matchedCity;

  const locationParts = cleaned
    .split(/,|\||\/|;/)
    .map((part) => part.replace(/\bIndia\b/gi, "").replace(/\s+/g, " ").trim())
    .filter((part) => /[a-z]/i.test(part) && !/\d/.test(part));

  return locationParts.at(-1) || cleaned;
}

function decodeHtml(value = "") {
  if (typeof document === "undefined") return value.replace(/&amp;/g, "&").replace(/&nbsp;/g, " ");
  const textarea = document.createElement("textarea");
  textarea.innerHTML = value;
  return textarea.value;
}

function htmlToText(html = "") {
  const decoded = decodeHtml(
    html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+\n/g, "\n")
      .replace(/\n\s+/g, "\n")
      .replace(/[ \t]{2,}/g, " ")
  );

  return decoded
    .replace(/\[(\/)?vc_[^\]]*\]/gi, " ")
    .replace(/\[(\/)?wp:[^\]]*\]/gi, " ")
    .replace(/\[[^\]]*(?:vc_row|vc_column|vc_column_text|traveljobad)[^\]]*\]/gi, " ")
    .replace(/(?:^|\n)[^\n]*(?:vc_row|vc_column|vc_column_text|traveljobad)[^\n]*(?=\n|$)/gi, "\n")
    .replace(/\s+\n/g, "\n")
    .replace(/\n\s+/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .trim();
}

function extractJobField(text, labels) {
  const lines = text.split(/\n+/);
  for (const line of lines) {
    const cleanLine = line.replace(/\s+/g, " ").trim();
    for (const label of labels) {
      const escapedLabel = label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const match = cleanLine.match(new RegExp(`^${escapedLabel}\\s*:\\s*(.+)$`, "i"));
      if (match?.[1]) return match[1].replace(/\s+/g, " ").trim();
    }
  }
  return "";
}

function removeBracketedText(value = "") {
  return value
    .replace(/\s*[\[(][^\])]*[\])]/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanJobFieldValue(value = "") {
  const cleaned = removeBracketedText(value).replace(/\s+/g, " ").trim();
  if (
    !cleaned ||
    /\b(preferred|must have|should have|candidate should|experience in|knowledge in|background in|ability to)\b/i.test(cleaned)
  ) {
    return "";
  }
  return cleaned;
}

function getCleanPostTitle(post) {
  return decodeHtml(post.title?.rendered || "")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanJobTitle(title = "") {
  return removeBracketedText(title)
    .replace(/\s+(?:Job Opening|Job|Opening)\b.*$/i, "")
    .replace(/\s+[–—-]\s+.*$/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

function cleanCardSummary(summary = "") {
  const cleaned = summary.replace(/\s+/g, " ").trim();
  if (
    !cleaned ||
    /\b(preferred|must have|should have|candidate should|experience in managing|managing multiple locations|eligibility|requirements?)\b/i.test(cleaned)
  ) {
    return "";
  }
  return cleaned;
}

function getJobSummary(post, text) {
  const excerpt = cleanCardSummary(htmlToText(post.excerpt?.rendered || "").replace(/^Job Summary:\s*/i, "").trim());
  if (excerpt) return excerpt;

  return (
    text
      .split(/\n+/)
      .map((line) => line.replace(/\s+/g, " ").trim())
      .find((line) =>
        line.length > 80 &&
        cleanCardSummary(line) &&
        !/^(table of contents|job details|about the role|key responsibilities|apply now|apply for this job)$/i.test(line) &&
        !/^(company name|job role|job type|experience required|location|department)\s*:/i.test(line)
      ) || ""
  );
}

function getAboutCompany(contentHtml = "") {
  const match = contentHtml.match(
    /<h[1-6][^>]*>[\s\S]*?About\s+Company:?[\s\S]*?<\/h[1-6]>\s*<p[^>]*>([\s\S]*?)<\/p>/i
  );
  const paragraph = match?.[1] ? htmlToText(match[1]) : "";
  return paragraph
    .replace(/^About Company:?\s*/i, "")
    .replace(/\s+/g, " ")
    .trim();
}

function getCompanyFromAbout(aboutCompany = "") {
  const cleanAbout = aboutCompany.replace(/\s+/g, " ").trim();
  const match = cleanAbout.match(
    /^(.{2,90}?\b(?:Pvt\.?\s+Ltd|Private\s+Limited|LLP|Ltd\.?|Limited|Holidays?|Travels?|Tours?|Tourism|Hospitality)\b)\s+(?:is|are|was|has|offers|specializes)\b/i
  );
  return match?.[1]?.replace(/\s+/g, " ").trim() || "";
}

function getPostedDaysAgo(dateValue) {
  const published = new Date(dateValue);
  if (Number.isNaN(published.getTime())) return undefined;
  const diff = Date.now() - published.getTime();
  return Math.max(0, Math.floor(diff / 86400000));
}

function getEmbeddedTerms(post) {
  return post?._embedded?.["wp:term"]?.flat?.() || [];
}

function getCategoryLabel(post, text) {
  const categoryLabels = getCategoryLabels(post, text);
  return categoryLabels[0] || "Hospitality & Tourism";
}

function getJobCategoryIds(post) {
  return (post.categories || []).filter((id) => WP_JOB_CATEGORY_BY_ID[id]);
}

function getCategoryLabels(post, text) {
  const termLabels = getEmbeddedTerms(post)
    .filter((term) => term.taxonomy === "category")
    .map((term) => decodeHtml(term.name))
    .filter((name) => name === "Hospitality & Tourism" || Object.prototype.hasOwnProperty.call(WP_JOB_CATEGORY_IDS, name));
  const idLabels = (post.categories || []).map((id) => WP_JOB_CATEGORY_BY_ID[id]).filter(Boolean);
  const categoryLabels = Array.from(new Set([...termLabels, ...idLabels]));
  if (categoryLabels.length) return categoryLabels;

  const lower = text.toLowerCase();
  if (lower.includes("sales")) return ["Sales"];
  if (lower.includes("human resource") || lower.includes("hr ")) return ["Human Resource"];
  if (lower.includes("marketing")) return ["Marketing"];
  if (lower.includes("finance") || lower.includes("account")) return ["Finance"];
  if (lower.includes("technology") || lower.includes("developer")) return ["Technology"];
  if (lower.includes("operation")) return ["Operations"];
  if (lower.includes("customer")) return ["Customer Service"];
  return ["Hospitality & Tourism"];
}

function getPostImage(post) {
  const media = post?._embedded?.["wp:featuredmedia"]?.[0];
  return media?.source_url || media?.media_details?.sizes?.medium?.source_url || "";
}

function getSkills(text, tags = []) {
  const preferred = extractJobField(text, ["Preferred Skills", "Skills"]);
  const fromField = preferred
    ? preferred.split(/,|•|·|\n/).map((skill) => skill.trim()).filter(Boolean)
    : [];
  const fromTags = tags.map((tag) =>
    removeBracketedText(decodeHtml(tag.name))
      .replace(/\bjobs?\b.*$/i, "")
      .replace(/\s+/g, " ")
      .trim()
  );
  const fallback = ["Communication Skills", "MS Office", "Negotiation", "Presentation skills"];
  return Array.from(new Set([...fromField, ...fromTags, ...fallback].map(toTitleCase)))
    .filter((skill) => skill.length > 2 && !/career|job opening|hospitality jobs/i.test(skill))
    .slice(0, 8);
}

function toTitleCase(value = "") {
  return value
    .toLowerCase()
    .replace(/\b[a-z]/g, (letter) => letter.toUpperCase())
    .replace(/\b(Ms|Hr|F&b|Seo|Ui|Ux)\b/g, (match) => match.toUpperCase());
}

function normalizeWpJob(post) {
  const contentHtml = post.content?.rendered || "";
  const text = htmlToText(contentHtml);
  const tags = getEmbeddedTerms(post).filter((term) => term.taxonomy === "post_tag");
  const categoryLabels = getCategoryLabels(post, text);
  const category = categoryLabels[0] || "Hospitality & Tourism";
  const categorySlugs = categoryLabels.map(toJobCategorySlug);
  const categoryIds = getJobCategoryIds(post);
  const postTitle = getCleanPostTitle(post);
  const aboutCompany = getAboutCompany(contentHtml);
  const company = extractJobField(text, ["Company Name", "Company"]) ||
    getCompanyFromAbout(aboutCompany) ||
    postTitle.match(/\bat\s+(.+)$/i)?.[1]?.replace(/\s+Job.*$/i, "") ||
    "Travel RethinkWays";
  const title = cleanJobTitle(extractJobField(text, ["Job Role", "Role", "Position", "Designation"]) || postTitle);

  return {
    id: post.id,
    wpId: post.id,
    title,
    company,
    companyInitial: company[0]?.toUpperCase() || "?",
    category,
    categorySlug: toJobCategorySlug(category),
    categoryLabels,
    categorySlugs,
    categoryIds,
    location: normalizeJobLocation(cleanJobFieldValue(extractJobField(text, ["Location", "Work Location"]))),
    type: extractJobField(text, ["Job Type", "Employment Type"]) || "Full-time",
    mode: /remote/i.test(text) ? "Remote" : /hybrid/i.test(text) ? "Hybrid" : "On-site",
    exp: cleanJobFieldValue(extractJobField(text, ["Experience Required", "Experience"])),
    salary: cleanJobFieldValue(extractJobField(text, ["Salary", "Compensation", "CTC"])),
    education: cleanJobFieldValue(extractJobField(text, ["Education", "Qualification"])),
    shift: cleanJobFieldValue(extractJobField(text, ["Shift"])),
    joining: cleanJobFieldValue(extractJobField(text, ["Joining", "Notice Period"])),
    postedDaysAgo: getPostedDaysAgo(post.date),
    date: post.date,
    link: post.link,
    logo: getPostImage(post),
    summary: getJobSummary(post, text),
    aboutCompany,
    skills: getSkills(text, tags),
    topPick: category === "Hospitality & Tourism" || category === "Sales",
  };
}

const FALLBACK_LOGOS = [
  "https://img.naukimg.com/logo_images/groups/v1/6317683.gif",
  "https://img.naukimg.com/logo_images/groups/v1/1033534.gif",
  "https://img.naukimg.com/logo_images/groups/v1/918690.gif",
  "https://img.naukimg.com/logo_images/groups/v1/8315513.gif",
];

function getLogoUrl(companyName) {
  if (!companyName) return FALLBACK_LOGOS[0];
  let hash = 0;
  for (let i = 0; i < companyName.length; i++) {
    hash = (hash * 31 + companyName.charCodeAt(i)) >>> 0;
  }
  return FALLBACK_LOGOS[hash % FALLBACK_LOGOS.length];
}

function CompanyLogo({ companyName, initial, logoUrl: customLogoUrl, size = "md", className = "", style }) {
  const [imgError, setImgError] = useState(false);
  const logoUrl = customLogoUrl || `${import.meta.env.BASE_URL}assets/images/jobs/Travel-Rethinkways-Tripzee-travels.jpg`;
  const showLogo = !!logoUrl && !imgError;

  const sizeClasses = {
    xs:  "h-4 w-4",
    sm:  "h-5 w-5",
    md:  "h-6 w-6",
    lg:  "h-8 w-8",
    xl:  "h-10 w-10",
  };

  const textSizeClasses = {
    xs:  "text-[9px]",
    sm:  "text-[10px]",
    md:  "text-[16px]",
    lg:  "text-[13px]",
    xl:  "text-[15px]",
  };

  return (
    <span
      className={[
        "inline-flex items-center justify-center rounded overflow-hidden flex-shrink-0",
        sizeClasses[size],
        showLogo
          ? "bg-white"
          : "bg-black text-white font-semibold",
        !showLogo && textSizeClasses[size],
        className,
      ].filter(Boolean).join(" ")}
      style={style}
      aria-hidden="true"
    >
      {showLogo ? (
        <img
          src={logoUrl}
          alt=""
          className={["object-contain"]}
          onError={() => setImgError(true)}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
        />
      ) : (
        initial || companyName?.[0]?.toUpperCase() || "?"
      )}
    </span>
  );
}

function uniqCompanies(items) {
  const map = new Map();
  items.forEach((job) => {
    if (!map.has(job.company)) {
      map.set(job.company, {
        name: job.company,
        initial: job.companyInitial || job.company?.[0]?.toUpperCase() || "?",
      });
    }
  });
  return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
}

function classNames(...parts) {
  return parts.filter(Boolean).join(" ");
}

function normalizeCompanyKey(value = "") {
  return decodeHtml(value)
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

const NoScrollbarCss = `
.no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
.no-scrollbar::-webkit-scrollbar { display: none; }
`;

// ─── FIX 4: CompaniesPage — no sidebar, jobs in split-card 2-col grid ────────
function CompaniesPage({ darkMode = false, jobs = [], title = "Top companies hiring now", subtitle = "", loading = false, error = "" }) {
  const isDark = darkMode;

  const bg = isDark ? "#000" : "#fff";
  const fg = isDark ? "#fff" : "#000";
  const muted = isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)";
  const filteredJobs = useMemo(() => jobs, [jobs]);

  return (
    <div style={{ background: bg, color: fg, minHeight: "100vh", fontFamily: "'Poppins', sans-serif" }}>
      <div style={{ maxWidth: 1500, margin: "0 auto", padding: "22px 24px 0" }}>
        <h2
          style={{
            margin: "0 0 14px",
            paddingTop: 18,
            paddingBottom: 10,
            fontSize: 40,
            lineHeight: 1.1,
            fontWeight: 700,
            color: fg,
            textAlign: "center",
            fontFamily: "'Poppins', Helvetica, Arial, sans-serif",
          }}
        >
          {title}
        </h2>
        {subtitle ? (
          <p style={{ fontSize: 16, color: muted, margin: "-4px 0 14px", textAlign: "center" }}>
            {subtitle}
          </p>
        ) : null}

        {/* Category buckets removed */}
        {/* <div style={{ display: "flex", justifyContent: "center", gap: 16, overflowX: "auto", paddingBottom: 6 }}>
          {topCompanyBuckets.map((b) => (
            <button
              key={b.label}
              type="button"
              onClick={() => setTopSearchQuery(topSearchQuery === b.label ? "" : b.label)}
              style={{
                minWidth: 200,
                background: topSearchQuery === b.label
                  ? isDark ? "#fff" : "#111"
                  : isDark ? "#0b0b0b" : "#fff",
                border: `1px solid ${topSearchQuery === b.label ? (isDark ? "#fff" : "#111") : cardBorder}`,
                borderRadius: 14,
                padding: "16px 18px",
                textAlign: "left",
                cursor: "pointer",
                flex: "0 0 auto",
                transition: "all 0.15s",
              }}
            >
              <div style={{
                fontSize: 18,
                fontWeight: 800,
                color: topSearchQuery === b.label ? (isDark ? "#000" : "#fff") : "#d02525",
                marginBottom: 6,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}>
                <span>{b.label}</span>
                <span style={{ fontWeight: 700, fontSize: 16 }}>›</span>
              </div>
              <div style={{
                fontSize: 13,
                color: topSearchQuery === b.label
                  ? isDark ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.75)"
                  : muted,
                fontWeight: 500,
              }}>{b.subtitle}</div>
            </button>
          ))}
        </div> */}

      </div>

      {/* ── FIX 4: Jobs grid using split layout, no sidebar ── */}
      <div style={{ maxWidth: 1500, margin: "0 auto", padding: "20px 24px 48px" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: muted }}>
            <p style={{ fontSize: 18, marginBottom: 8 }}>Loading live jobs from Travel RethinkWays...</p>
          </div>
        ) : error ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: muted }}>
            <p style={{ fontSize: 18, marginBottom: 8 }}>Unable to load live jobs: {error}</p>
          </div>
        ) : filteredJobs.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px 0", color: muted }}>
            <p style={{ fontSize: 18, marginBottom: 8 }}>No jobs match your filters.</p>
          </div>
        ) : (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 16 }}>
              {filteredJobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  bookmarked={false}
                  isDark={isDark}
                  layout="split"
                  variant="recent"
                  actionsOnCard
                  hiddenSkills={["Client relationship management"]}
                  onToggleBookmark={() => {}}
                />
              ))}
            </div>
            <p style={{ fontSize: 13, color: muted, margin: "24px 0 0", textAlign: "center" }}>
              Showing {filteredJobs.length} of {jobs.length} jobs
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function SectionTitle({ children, isDark }) {
  return (
    <h2
      className={classNames(
        "text-[54px] leading-[1.05] font-poppins",
        isDark ? "text-white" : "text-black"
      )}
    >
      {children}
    </h2>
  );
}

function CompanyChip({ active, label, count, initial, companyName, onClick, isDark }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "group inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[13px] font-medium transition",
        active
          ? isDark
            ? "bg-white text-black"
            : "bg-black text-white"
          : isDark
          ? "bg-white/90 text-black hover:bg-white"
          : "bg-[#f8f9fa] text-black hover:bg-white"
      )}
    >
      {companyName && companyName !== "All companies" ? (
        <CompanyLogo
          companyName={companyName}
          initial={initial}
          size="md"
          className={active
            ? isDark ? "border-white/20" : "border-black/20"
            : ""}
        />
      ) : (
        <span
          className={classNames(
            "inline-flex h-6 w-6 items-center justify-center rounded-full text-[16px] font-semibold",
            active
              ? isDark ? "bg-black text-white" : "bg-white text-black"
              : isDark ? "bg-white text-black" : "bg-black text-white"
          )}
          aria-hidden="true"
        >
          {initial}
        </span>
      )}
      <span className="whitespace-nowrap">{label}</span>
      {typeof count === "number" && (
        <span
          className={classNames(
            "ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px]",
            active
              ? isDark ? "bg-black/10 text-black" : "bg-white/15 text-white"
              : isDark ? "bg-white/10 text-white" : "bg-black/10 text-black"
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

function JobTitleChip({ active, label, icon, iconBg, onClick, isDark }) {
  const chipBg = iconBg || (isDark ? "rgba(255, 255, 255, 0.92)" : "#f8f9fa");

  return (
    <button
      type="button"
      onClick={onClick}
      style={icon ? { backgroundColor: chipBg } : undefined}
      className={classNames(
        "group inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-[16px] font-medium shadow-sm transition",
        active
          ? icon
            ? "text-black ring-2 ring-white/80"
            : isDark
            ? "text-black ring-2 ring-white/80"
            : "bg-black text-white"
          : isDark
          ? "text-black hover:bg-white"
          : "text-black hover:bg-white"
      )}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className="inline-flex h-8 w-8 items-center justify-center"
        >
          <img
            src={icon}
            alt=""
            className="h-4 w-4 opacity-80"
            loading="lazy"
            decoding="async"
            referrerPolicy="no-referrer"
          />
        </span>
      ) : null}
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

function Pill({ active, children, icon, onClick, isDark }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "inline-flex items-center gap-2 rounded-lg px-4 py-2 text-[14px] font-semibold transition",
        active
          ? "bg-[#d02525] text-white"
          : isDark
          ? "bg-white/5 text-white hover:bg-white/10"
          : "bg-black/5 text-black hover:bg-black/10"
      )}
    >
      {icon ? (
        <img
          src={icon}
          alt=""
          className={classNames("h-4 w-4", active ? "brightness-0 invert" : isDark ? "invert opacity-80" : "opacity-70")}
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
        />
      ) : null}
      {children}
    </button>
  );
}

// ─── BookmarkButton & ShareButton helpers (shared by both layouts) ─────────────
function BookmarkButton({ bookmarked, onToggleBookmark, isDark }) {
  return (
    <button
      type="button"
      aria-label={bookmarked ? "Remove bookmark" : "Bookmark job"}
      onClick={onToggleBookmark}
      className={classNames(
        "inline-flex h-10 w-10 items-center justify-center rounded-full border transition",
        bookmarked
          ? "border-[#d02525] bg-[#d02525] text-white"
          : isDark
          ? "border-white/20 bg-black text-white hover:bg-white/10"
          : "border-black/20 bg-white text-black hover:bg-black/5"
      )}
    >
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M7 3h10a2 2 0 0 1 2 2v16l-7-4-7 4V5a2 2 0 0 1 2-2Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinejoin="round"
          fill={bookmarked ? "currentColor" : "none"}
        />
      </svg>
    </button>
  );
}

function ShareButton() {
  return (
    <button
      type="button"
      aria-label="Share job"
      className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#25D366] bg-[#25D366] text-white transition hover:bg-[#1ebe5d]"
    >
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 2l-7 20-4-9-9-4 20-7Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function NorthEastArrow() {
  return <i aria-hidden="true" className="fa-solid fa-arrow-up-right ml-2 text-[0.9em]" />;
}

// ─── JobCard — FIX 1: bookmark/share absolutely positioned top-right ──────────
function JobCard({ job, bookmarked, onToggleBookmark, isDark, layout = "compact", variant = "default", hiddenSkills = [], recentLogoSize = 200, recentStacked = false, actionsOnCard = false }) {
  const categoryLabel = (() => {
    if (job.category) return job.category;
    const text = `${job.title || ""} ${job.company || ""}`.toLowerCase();
    const travelKeywords = ["travel", "tour", "trip", "holiday", "visa", "flight", "air", "airline", "airport", "cabin", "crew", "ticket"];
    return travelKeywords.some((k) => text.includes(k)) ? "Travel" : "Hospitality";
  })();
  const postedLabel =
    typeof job.postedDaysAgo === "number" ? `Posted ${job.postedDaysAgo}d ago` : "Recently posted";
  const hiddenSkillSet = new Set(hiddenSkills);
  const skills = (
    Array.isArray(job.skills) && job.skills.length
      ? job.skills.slice(0, 6)
      : ["Client relationship management", "Negotiation", "MS Office", "Presentation skills", "Communication Skills", "Automation", "Accounting", "Reconciliation"]
  ).filter((skill) => !hiddenSkillSet.has(skill));
  const displayTag = variant === "recent" ? "Hiring" : categoryLabel;
  const recentSummary = cleanCardSummary(
    job.summary ||
    job.description ||
    ""
  );
  const recentMeta = [job.exp, job.salary, job.location].filter(Boolean);
  const compactMeta = [job.mode, job.salary, postedLabel].filter(Boolean);
  const detailItems = [
    { label: "Experience", value: job.exp },
    { label: "Education", value: job.education },
    { label: "Shift", value: job.shift },
    { label: "Joining", value: job.joining },
    { label: "Location", value: job.location },
  ].filter((item) => item.value);

  if (layout === "split") {
    if (variant === "recent") {
      return (
        <article className="relative flex h-full min-h-[330px] w-full flex-col rounded-2xl bg-[#F8F9FA] p-5 text-black">
          {actionsOnCard ? (
            <div className="absolute right-5 top-5 z-20 flex shrink-0 justify-end gap-2">
              <BookmarkButton bookmarked={bookmarked} onToggleBookmark={onToggleBookmark} isDark={false} />
              <ShareButton />
            </div>
          ) : null}
          <div
            className={classNames(
              "grid flex-1 items-start gap-5",
              recentStacked ? "grid-cols-1" : "md:grid-cols-[var(--recent-logo-grid)]"
            )}
            style={{ "--recent-logo-grid": `${recentLogoSize}px 1fr` }}
          >
            <div className="w-full">
              <div
                className="relative flex w-full items-center justify-center overflow-hidden rounded-2xl bg-white"
                style={{ height: recentLogoSize }}
              >
                <span className="absolute left-4 top-0 z-10 inline-flex bg-[#d02525] px-3 py-2 text-[12px] font-bold uppercase tracking-[0.08em] text-white">
                  {displayTag}
                </span>
                {!actionsOnCard ? (
                  <div className="absolute left-auto right-3 top-3 z-10 flex shrink-0 justify-end gap-2">
                    <BookmarkButton bookmarked={bookmarked} onToggleBookmark={onToggleBookmark} isDark={false} />
                    <ShareButton />
                  </div>
                ) : null}
                <CompanyLogo
                  companyName={job.company}
                  initial={job.companyInitial || job.company?.[0]?.toUpperCase() || "?"}
                  logoUrl={job.logo}
                  size="lg"
                  className="object-contain"
                  style={{ height: recentLogoSize, width: recentLogoSize }}
                />
              </div>
            </div>

            <div className={classNames("min-w-0", actionsOnCard ? "pr-[96px] sm:pr-[112px]" : "")}>
              <p className="m-0 mb-1 truncate text-[14px] font-semibold text-[#4b5578]">
                {job.company}
              </p>

              <h3 className="m-0 line-clamp-2 text-[18px] font-extrabold leading-[1.2] text-black">
                {job.title}
              </h3>

              {recentMeta.length ? (
                <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[14px] font-semibold text-[#424b6f]">
                  {recentMeta.map((item, index) => (
                    <span key={item} className="inline-flex items-center gap-x-4">
                      {index > 0 ? <span className="text-black/20">|</span> : null}
                      <span>{item}</span>
                    </span>
                  ))}
                </div>
              ) : null}

              {recentSummary ? (
                <p className="mt-3 line-clamp-1 text-[13px] font-medium text-[#424b6f]">
                  {recentSummary}
                </p>
              ) : null}

              <div className="mt-3 flex max-h-[48px] flex-wrap gap-x-2 gap-y-1 overflow-hidden text-[12px] font-medium leading-5 text-[#53618b]">
                {skills.slice(0, 6).map((skill) => (
                  <span key={skill}>{skill} <span className="text-[#53618b]/60">·</span></span>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-auto flex items-center justify-between gap-4 pt-5">
            <p className="m-0 text-[12px] font-medium text-[#53618b]">
              {postedLabel}
            </p>
            <button type="button" onClick={() => job.link && window.open(job.link, "_blank", "noopener,noreferrer")} className="inline-flex h-10 min-w-[170px] items-center justify-center rounded-full bg-[#d02525] px-6 text-[16px] font-semibold text-white">
              View Job Detail <NorthEastArrow />
            </button>
          </div>
        </article>
      );
    }

    return (
      <article
        className={classNames(
          "relative w-full overflow-hidden rounded-2xl bg-[#F8F9FA] p-5 text-black"
        )}
      >
        <div className="grid gap-5 md:grid-cols-[200px_1fr]">
          <div className="w-full">
            <div className="relative flex h-[160px] w-full items-center justify-center overflow-hidden rounded-2xl bg-white">
              <span className="absolute left-4 top-0 z-10 inline-flex bg-[#d02525] px-3 py-2 text-[12px] font-bold uppercase tracking-[0.08em] text-white">
                {displayTag}
              </span>
              <CompanyLogo
                companyName={job.company}
                initial={job.companyInitial || job.company?.[0]?.toUpperCase() || "?"}
                logoUrl={job.logo}
                size="lg"
                className="h-[120px] w-[170px] object-contain"
              />
            </div>
          </div>

          <div className="min-w-0">
            <div className="mb-4 flex items-start justify-between gap-3">
              <h3 className="m-0 min-w-0 flex-1 line-clamp-2 text-[22px] font-extrabold leading-[1.15] text-black">
                {job.title}
              </h3>
              <div className="flex shrink-0 gap-2">
                <BookmarkButton bookmarked={bookmarked} onToggleBookmark={onToggleBookmark} isDark={false} />
                <ShareButton />
              </div>
            </div>

            {compactMeta.length ? (
              <p className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[14px] font-semibold text-black/70">
                {compactMeta.map((item, index) => (
                  <span key={item} className="inline-flex items-center gap-x-3">
                    {index > 0 ? <span className="text-black/20">|</span> : null}
                    <span>{item}</span>
                  </span>
                ))}
              </p>
            ) : null}

            <div className="mt-4">
              <p className="m-0 text-[16px] font-extrabold text-black">Skills:</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {skills.slice(0, 4).map((s) => (
                  <span
                    key={s}
                    className="rounded-full bg-black/5 px-3 py-1.5 text-[13px] font-semibold text-black"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>

          </div>
        </div>

        <div className="mt-5 grid items-center gap-4 md:grid-cols-[3fr_1fr]">
          <div className="flex min-w-0 flex-wrap items-center gap-x-5 gap-y-3">
            {detailItems.map((item) => (
              <div key={item.label} className="whitespace-nowrap">
                <div className="text-[14px] font-extrabold text-black">{item.label}</div>
                <div className="mt-0.5 text-[13px] text-black/70">{item.value}</div>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <button type="button" onClick={() => job.link && window.open(job.link, "_blank", "noopener,noreferrer")} className="inline-flex h-11 w-full items-center justify-center rounded-full bg-[#d02525] px-6 text-[16px] font-semibold text-white md:max-w-[240px]">
              View Job Detail <NorthEastArrow />
            </button>
          </div>
        </div>
      </article>
    );
  }

  // ── compact layout — FIX 1: bookmark/share absolutely top-right ──
  return (
    <article
      className={classNames(
        "relative w-full overflow-hidden rounded-2xl bg-[#F8F9FA] p-5 text-black"
      )}
    >
      <div className="flex flex-col gap-5">
        {/* Logo */}
        <div className="flex w-full items-center justify-center">
          <div className="relative flex h-[210px] w-full items-center justify-center overflow-hidden rounded-2xl bg-white">
            <span className="absolute left-4 top-0 z-10 inline-flex bg-[#d02525] px-3 py-2 text-[12px] font-bold uppercase tracking-[0.08em] text-white">
              {displayTag}
            </span>
            <div className="absolute right-3 top-3 z-10 flex items-center gap-2">
              <BookmarkButton bookmarked={bookmarked} onToggleBookmark={onToggleBookmark} isDark={false} />
              <ShareButton />
            </div>
            <CompanyLogo
              companyName={job.company}
              initial={job.companyInitial || job.company?.[0]?.toUpperCase() || "?"}
              logoUrl={job.logo}
              size="lg"
              className="h-[110px] w-[260px] object-contain"
            />
          </div>
        </div>

        <div className="min-w-0">
          <h3 className="mt-2 line-clamp-2 text-[22px] font-extrabold leading-[1.15] text-black">
            {job.title}
          </h3>

          {compactMeta.length ? (
            <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[16px] font-semibold text-black/70">
              {compactMeta.map((item, index) => (
                <span key={item} className="inline-flex items-center gap-x-3">
                  {index > 0 ? <span className="text-black/20">|</span> : null}
                  <span>{item}</span>
                </span>
              ))}
            </p>
          ) : null}

          <div className="mt-4">
            <p className="m-0 text-[18px] font-extrabold text-black">Skills:</p>
            <div className="mt-3 flex flex-wrap gap-3">
              {skills.map((s) => (
                <span key={s} className="rounded-full bg-black/5 px-4 py-2 text-[14px] font-semibold text-black">
                  {s}
                </span>
              ))}
            </div>
          </div>

          {[
              {
                label: "Experience",
                value: job.exp,
                icon: (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 14l2 2 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
              },
              {
                label: "Education",
                value: job.education,
                icon: (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 10l-10 5L2 10l10-5 10 5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                    <path d="M6 12v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ),
              },
              {
                label: "Shift",
                value: job.shift,
                icon: (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20Z" stroke="currentColor" strokeWidth="2" />
                    <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
              },
              {
                label: "Joining",
                value: job.joining,
                icon: (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 3v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M17 3v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M3 8h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M5 6h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                ),
              },
            ].filter((item) => item.value).length ? (
            <div className="mt-5 grid grid-cols-2 gap-5">
            {[
              {
                label: "Experience",
                value: job.exp,
                icon: (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 14l2 2 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
              },
              {
                label: "Education",
                value: job.education,
                icon: (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 10l-10 5L2 10l10-5 10 5Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                    <path d="M6 12v5c0 1.5 3 3 6 3s6-1.5 6-3v-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                ),
              },
              {
                label: "Shift",
                value: job.shift,
                icon: (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22a10 10 0 1 1 0-20 10 10 0 0 1 0 20Z" stroke="currentColor" strokeWidth="2" />
                    <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ),
              },
              {
                label: "Joining",
                value: job.joining,
                icon: (
                  <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M7 3v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M17 3v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M3 8h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    <path d="M5 6h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  </svg>
                ),
              },
            ].filter((item) => item.value).map((item) => (
              <div key={item.label} className="flex items-start gap-3">
                <span className="mt-0.5 text-black/70">{item.icon}</span>
                <div>
                  <div className="text-[18px] font-extrabold text-black">{item.label}</div>
                  <div className="mt-1 text-[16px] text-black/70">{item.value}</div>
                </div>
              </div>
            ))}
          </div>
          ) : null}

          <div className="mt-6">
            <button type="button" onClick={() => job.link && window.open(job.link, "_blank", "noopener,noreferrer")} className="inline-flex h-12 w-full items-center justify-center rounded-full bg-[#d02525] px-8 text-[16px] font-semibold text-white">
              View Job Detail <NorthEastArrow />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

function SponsoredFilterPill({ active, label, onClick, isDark }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={classNames(
        "rounded-full border px-4 py-2 text-[16px] font-semibold transition",
        active
          ? isDark
            ? "border-white bg-white text-black"
            : "border-black bg-black text-white"
          : isDark
          ? "border-white/20 bg-transparent text-white hover:bg-white/10"
          : "border-black/20 bg-transparent text-black hover:bg-black/5"
      )}
    >
      {label}
    </button>
  );
}

function SponsoredCompanyCard({ company, isDark }) {
  return (
    <article
      className={classNames(
        "flex h-full w-full flex-col items-center justify-center rounded-2xl border p-6 text-center",
        isDark ? "border-white/15 bg-black text-white" : "border-black/10 bg-white text-black"
      )}
    >
      <div
        className={classNames(
          "flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl border",
          isDark ? "border-white/10 bg-white" : "border-black/10 bg-white"
        )}
      >
        <img
          src={company.logo}
          alt={company.name}
          className="h-14 w-14 object-contain"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
        />
      </div>

      <h3 className={classNames("mt-4 text-[21px] font-extrabold", isDark ? "text-white" : "text-black")}>
        {company.name}
      </h3>

      <div className={classNames("mt-3 flex items-center gap-2 text-[16px]", isDark ? "text-white/70" : "text-black/60")}>
        <span className="text-[#d02525]">★</span>
        <span className="font-semibold">{Number(company.rating || 0).toFixed(1)}</span>
        <span className={isDark ? "text-white/25" : "text-black/20"}>·</span>
        <span>{company.reviews}</span>
      </div>

      {company.tags?.length ? (
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {company.tags.slice(0, 5).map((t) => (
            <span
              key={`${company.id}-${t}`}
              className="rounded-full bg-[#d02525] px-3 py-1 text-[14px] font-semibold text-white"
            >
              {t}
            </span>
          ))}
        </div>
      ) : null}
    </article>
  );
}

function FeaturedCompanyCard({ company, isDark }) {
  return (
    <article
      className={classNames(
        "flex h-full w-full flex-col items-center rounded-2xl bg-[#f8f9fa] p-5 text-center",
        isDark ? "text-black" : "text-black"
      )}
    >
      <div className="flex h-[170px] w-full items-center justify-center overflow-hidden rounded-2xl bg-white">
        <img
          src={company.logo}
          alt={company.name}
          className="h-[155px] w-[240px] object-contain"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
        />
      </div>

      <div className="flex min-h-0 w-full flex-1 flex-col items-center">
        <h3 className={classNames("m-0 mt-4 w-full truncate whitespace-nowrap text-[21px] font-extrabold", isDark ? "text-white" : "text-black")}>
          {company.name}
        </h3>
        <p className={classNames("mt-3 line-clamp-3 min-h-[65px] text-[16px] leading-[1.35]", isDark ? "text-white/70" : "text-black/60")}>
          {company.blurb}
        </p>
        <div className="mt-auto pt-6">
          <button
            type="button"
            onClick={company.onViewJobs}
            className="inline-flex h-10 min-w-[170px] items-center justify-center rounded-full bg-[#d02525] px-6 text-[16px] font-semibold text-white"
          >
            View Company <NorthEastArrow />
          </button>
        </div>
      </div>
    </article>
  );
}

function CompanyLanding({ companyName, isDark, onBack, jobsSource = [], loading = false }) {
  const decodedCompanyName = decodeURIComponent(companyName || "");
  const companyKey = normalizeCompanyKey(decodedCompanyName);
  const jobs = useMemo(
    () => jobsSource.filter((j) => normalizeCompanyKey(j.company) === companyKey),
    [companyKey, jobsSource]
  );
  const profile = useMemo(() => {
    const firstJob = jobs[0];
    return {
      employees: `${jobs.length || 0} live role${jobs.length === 1 ? "" : "s"}`,
      phone: "Listed on Travel RethinkWays",
      email: "See job post to apply",
      address: firstJob?.location || "India",
      about: firstJob?.summary || `${decodedCompanyName || "This company"} has live job openings listed on Travel RethinkWays.`,
      socials: {
        website: firstJob?.link || "https://travel.rethinkways.com/jobs/",
        linkedin: firstJob?.link || "https://travel.rethinkways.com/jobs/",
        instagram: firstJob?.link || "https://travel.rethinkways.com/jobs/",
        x: firstJob?.link || "https://travel.rethinkways.com/jobs/",
      },
      banner:
        "https://images.unsplash.com/photo-1488646953014-85cb44e25828?auto=format&fit=crop&w=2200&q=60",
    };
  }, [decodedCompanyName, jobs]);

  const IconLink = ({ href, label, children }) => (
    <a
      href={href}
      aria-label={label}
      target="_blank"
      rel="noreferrer"
      className={classNames(
        "inline-flex h-10 w-10 items-center justify-center rounded-full border transition",
        isDark ? "border-white/15 bg-white/5 text-white hover:bg-white/10" : "border-black/10 bg-black/5 text-black hover:bg-black/10"
      )}
    >
      {children}
    </a>
  );

  return (
    <div className={classNames("w-full", isDark ? "bg-black text-white" : "bg-white text-black")}>
      <div className="relative left-1/2 w-[100dvw] max-w-[100dvw] -translate-x-1/2 overflow-hidden">
        <div
          className="h-[240px] w-full bg-cover bg-center"
          style={{ backgroundImage: `url('${profile.banner}')` }}
        />
        <div
          aria-hidden="true"
          className={classNames(
            "absolute inset-0",
            isDark ? "bg-black/55" : "bg-black/35"
          )}
        />
      </div>

      <div className="mx-auto w-full max-w-[1500px] px-4 pb-14">
        {/* Header card (match screenshot): avatar + title + about on white panel */}
        <div className="-mt-16 rounded-2xl bg-white md:-mt-20">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div className="flex flex-col items-start gap-5">
              <div className="h-150 w-150 overflow-hidden rounded-full bg-white border-[3px] border-[#d02525] z-[9999]">
                <img
                  src={jobs[0]?.logo || getLogoUrl(decodedCompanyName)}
                  alt={decodedCompanyName}
                  className="h-full w-full object-cover"
                  loading="lazy"
                  decoding="async"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="pb-1">
                <h1 className="m-0 text-[34px] font-extrabold leading-[1.05] text-black">
                  {decodedCompanyName || "Company"}
                </h1>
                <p className="mt-2 max-w-[720px] text-[24px] leading-[1.35] text-black/60">
                  {profile.about}
                </p>
              </div>
            </div>

            <div className="flex flex-col items-end gap-3 md:pb-1">
                        <div className="flex flex-row items-end gap-4 md:grid-cols-4">
            {[
              { label: "Employees", value: profile.employees },
              { label: "Phone", value: profile.phone },
              { label: "Email", value: profile.email },
              { label: "Address", value: profile.address },
            ].map((item) => (
              <div key={item.label} className="min-w-0">
                <div className={classNames("text-[18px] font-semibold", isDark ? "text-white/60" : "text-black/60")}>{item.label}</div>
                <div className={classNames("mt-1 text-[16px] font-semibold", isDark ? "text-white" : "text-black")}>{item.value}</div>
              </div>
            ))}
          </div>

           <div className="mt-2 flex items-center gap-3 overflow-x-auto no-scrollbar">
            <IconLink href={profile.socials.website} label="Website">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z" stroke="currentColor" strokeWidth="2" />
                <path d="M3.6 9h16.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M3.6 15h16.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <path d="M12 3c2.6 2.6 4 5.7 4 9s-1.4 6.4-4 9c-2.6-2.6-4-5.7-4-9s1.4-6.4 4-9Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
              </svg>
            </IconLink>
            <IconLink href={profile.socials.linkedin} label="LinkedIn">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S0 4.88 0 3.5 1.12 1 2.5 1s2.48 1.12 2.48 2.5ZM0.5 23.5h4V7.98h-4V23.5ZM8.5 7.98h3.83v2.12h.05c.53-1 1.83-2.06 3.77-2.06 4.03 0 4.78 2.65 4.78 6.1v9.36h-4v-8.3c0-1.98-.04-4.53-2.76-4.53-2.76 0-3.18 2.15-3.18 4.39v8.44h-4V7.98Z" />
              </svg>
            </IconLink>
            <IconLink href={profile.socials.instagram} label="Instagram">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Z" stroke="currentColor" strokeWidth="2" />
                <path d="M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8Z" stroke="currentColor" strokeWidth="2" />
                <path d="M17.5 6.5h.01" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
              </svg>
            </IconLink>
            <IconLink href={profile.socials.x} label="X">
              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.9 2H22l-6.8 7.8L23 22h-6.7l-5.3-6.6L5.2 22H2l7.3-8.4L1 2h6.9l4.8 6.1L18.9 2Zm-1.2 18h1.7L7.4 3.9H5.6L17.7 20Z" />
              </svg>
            </IconLink>
          </div>
            </div>

            
          </div>
        </div>

        <div className="mt-12">
          <div className="mb-5 flex items-end justify-between gap-4">
            <h2 className={classNames("m-0 text-[36px] font-extrabold", isDark ? "text-white" : "text-black")}>
              {decodedCompanyName} is hiring
            </h2>
            <div className={classNames("text-[13px]", isDark ? "text-white/60" : "text-black/60")}>
              {jobs.length} jobs
            </div>
          </div>

          {loading ? (
            <div className={classNames("rounded-2xl border p-8 text-center", isDark ? "border-white/15 text-white/70" : "border-black/10 text-black/60")}>
              Loading live jobs from this company...
            </div>
          ) : jobs.length === 0 ? (
            <div className={classNames("rounded-2xl border p-8 text-center", isDark ? "border-white/15 text-white/70" : "border-black/10 text-black/60")}>
              No jobs found for this company.
            </div>
          ) : (
            <div className="grid grid-cols-1 items-stretch gap-4 md:grid-cols-2">
              {jobs.map((job) => (
                <JobCard
                  key={job.id}
                  job={job}
                  bookmarked={false}
                  isDark={isDark}
                  layout="split"
                  variant="recent"
                  actionsOnCard
                  hiddenSkills={["Client relationship management"]}
                  onToggleBookmark={() => {}}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PremiumJobCard({ job, isDark }) {
  return (
    <article
      className={classNames(
        "w-[250px] shrink-0 overflow-hidden rounded-2xl bg-[#F8F9FA] text-black"
      )}
    >
      <div className="h-[210px] w-full overflow-hidden bg-black/5">
        <img
          src={job.image}
          alt={job.title}
          className="h-full w-full object-cover"
          loading="lazy"
          decoding="async"
          referrerPolicy="no-referrer"
        />
      </div>

      <div className="p-5">
        <div className="flex flex-wrap items-center gap-2">
          <span className={classNames("inline-flex rounded-full px-3 py-1 text-[12px] font-semibold", isDark ? "bg-white/10 text-white/85" : "bg-black/5 text-black/70")}>
            Career insight
          </span>
          <span className={classNames("text-[12px]", isDark ? "text-white/55" : "text-black/45")}>
            {job.company}
          </span>
        </div>

        <h3 className={classNames("mt-3 line-clamp-2 text-[20px] font-extrabold leading-[1.2]", isDark ? "text-white" : "text-black")}>
          {job.title}
        </h3>

        <p className={classNames("mt-3 text-[16px]", isDark ? "text-white/70" : "text-black/60")}>
          {job.location} <span className={isDark ? "text-white/30" : "text-black/20"}>•</span> {job.exp}
        </p>
      </div>
    </article>
  );
}

function LocationCard({ item, isDark }) {
  return (
    <article className="relative h-[210px] w-[100%] shrink-0 overflow-hidden rounded-2xl">
      <img
        src={item.image}
        alt={item.name}
        className="h-full w-full object-cover"
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
      />
      <div className={classNames("absolute inset-0", isDark ? "bg-black/45" : "bg-black/35")} />
      <div className="absolute bottom-0 left-0 right-0 p-5">
        <h3 className="m-0 text-[21px] font-extrabold text-white">{item.name}</h3>
        <p className="mt-1 text-[16px] text-white/85">Open Jobs ({item.openJobs})</p>
      </div>
    </article>
  );
}

function HiringCategoryCard({ title, subtitle, logos, isDark }) {
  const chevronIcon =
    "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/chevron-right.svg";
  const placeholderIcon =
    "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/building-office-2.svg";

  return (
    <div
      className={classNames(
        "w-[260px] shrink-0 rounded-2xl border p-5",
        isDark ? "border-white/15 bg-black text-white" : "border-black/10 bg-white text-black"
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="m-0 text-[21px] font-extrabold leading-none text-[#d02525]">
              {title}
            </h3>
            <img
              src={chevronIcon}
              alt=""
              className={classNames("h-[16px] w-[16px]", isDark ? "opacity-70 invert" : "opacity-45")}
              loading="lazy"
              decoding="async"
              referrerPolicy="no-referrer"
            />
          </div>
          <p className={classNames("mt-2 text-[16px] leading-[1.2]", isDark ? "text-white/70" : "text-black/60")}>
            {subtitle}
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        {[0, 1, 2, 3].map((idx) => (
          <LogoTile
            key={`${title}-logo-${idx}`}
            src={logos[idx]}
            placeholder={placeholderIcon}
            isDark={isDark}
          />
        ))}
      </div>
    </div>
  );
}

function LogoTile({ src, placeholder, isDark }) {
  const [imgError, setImgError] = useState(false);
  const showReal = src && !imgError;

  return (
    <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-xl bg-white">
      <img
        src={showReal ? src : placeholder}
        alt=""
        className={classNames(
          "h-10 w-10 object-contain",
          showReal ? "" : isDark ? "opacity-55" : "opacity-35"
        )}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
        onError={() => setImgError(true)}
      />
    </div>
  );
}

export default function Jobs({ darkMode = false }) {
  const routeLocation = useLocation();
  const navigate = useNavigate();
  const isDark = !!darkMode;
  const [query, setQuery] = useState("");
  const [experience, setExperience] = useState("0-2 years");
  const [location, setLocation] = useState("");
  const [jobType, setJobType] = useState("All");
  const [mode, setMode] = useState("");
  const [liveJobs, setLiveJobs] = useState(() => liveJobsCache.data || []);
  const [jobsLoading, setJobsLoading] = useState(() => !liveJobsCache.data);
  const [jobsError, setJobsError] = useState("");
  const [bookmarks, setBookmarks] = useState(() => new Set());
  const [visibleFeaturedCompanies, setVisibleFeaturedCompanies] = useState(12);

  const pickNWithFallback = (primaryList, fallbackList, n) => {
    const out = [];
    const seen = new Set();
    const pushUnique = (job) => {
      if (!job || seen.has(job.id)) return;
      seen.add(job.id);
      out.push(job);
    };
    primaryList.forEach(pushUnique);
    fallbackList.forEach(pushUnique);
    return out.slice(0, n);
  };

  const jobsSource = liveJobs;
  const categoryLabels = useMemo(() => ["All", ...Object.keys(WP_JOB_CATEGORY_IDS)], []);
  const listingParams = useMemo(() => new URLSearchParams(routeLocation.search), [routeLocation.search]);
  const listingCategorySlug = useMemo(
    () => toJobCategorySlug(listingParams.get("category") || ""),
    [listingParams]
  );
  const listingCategory = useMemo(() => {
    if (!listingCategorySlug) return "";
    return categoryLabels.find((label) => label !== "All" && toJobCategorySlug(label) === listingCategorySlug) || "";
  }, [listingCategorySlug, categoryLabels]);
  const listingCategoryId = listingCategory ? WP_JOB_CATEGORY_IDS[listingCategory] : undefined;
  const listingQuery = (listingParams.get("q") || "").trim().toLowerCase();
  const listingLocation = (listingParams.get("location") || "").trim().toLowerCase();
  const listingExperience = (listingParams.get("experience") || "").trim().toLowerCase();
  const listingType = useMemo(() => {
    const typeParam = toJobCategorySlug(listingParams.get("type") || "");
    return JOB_TYPES.find((type) => type !== "All" && toJobCategorySlug(type) === typeParam) || "";
  }, [listingParams]);
  const listingMode = useMemo(() => {
    const modeParam = toJobCategorySlug(listingParams.get("mode") || "");
    return JOB_MODES.find((item) => toJobCategorySlug(item) === modeParam) || "";
  }, [listingParams]);
  const hasListingFilters = !!(listingCategorySlug || listingQuery || listingLocation || listingExperience || listingType || listingMode);
  const listingJobs = useMemo(() => {
    const experienceQuery = listingExperience.replace(" years", "");

    return jobsSource.filter((job) => {
      const jobCategorySlugs = job.categorySlugs?.length
        ? job.categorySlugs
        : [job.categorySlug || toJobCategorySlug(job.category)];
      const jobCategoryIds = job.categoryIds || [];
      if (
        listingCategorySlug &&
        !jobCategorySlugs.includes(listingCategorySlug) &&
        (!listingCategoryId || !jobCategoryIds.includes(listingCategoryId))
      ) {
        return false;
      }
      if (listingType && job.type !== listingType) return false;
      if (listingMode && job.mode !== listingMode) return false;
      if (listingLocation && !(job.location || "").toLowerCase().includes(listingLocation)) return false;
      if (experienceQuery && !(job.exp || "").toLowerCase().includes(experienceQuery)) return false;
      if (!listingQuery) return true;

      return (
        (job.title || "").toLowerCase().includes(listingQuery) ||
        (job.company || "").toLowerCase().includes(listingQuery) ||
        (job.location || "").toLowerCase().includes(listingQuery) ||
        (job.category || "").toLowerCase().includes(listingQuery) ||
        (job.categoryLabels || []).some((categoryLabel) => categoryLabel.toLowerCase().includes(listingQuery)) ||
        (job.skills || []).some((skill) => skill.toLowerCase().includes(listingQuery))
      );
    });
  }, [jobsSource, listingCategorySlug, listingCategoryId, listingQuery, listingLocation, listingExperience, listingType, listingMode]);
  const buildJobsListingPath = (nextFilters = {}) => {
    const params = new URLSearchParams();
    const filters = {
      q: query.trim(),
      location: location.trim(),
      experience: experience === "0-2 years" ? "" : experience,
      type: jobType !== "All" ? toJobCategorySlug(jobType) : "",
      mode: mode ? toJobCategorySlug(mode) : "",
      ...nextFilters,
    };

    if (filters.category) params.set("category", toJobCategorySlug(filters.category));
    if (filters.q) params.set("q", filters.q);
    if (filters.location) params.set("location", filters.location);
    if (filters.experience) params.set("experience", filters.experience);
    if (filters.type) params.set("type", toJobCategorySlug(filters.type));
    if (filters.mode) params.set("mode", toJobCategorySlug(filters.mode));

    const queryString = params.toString();
    return `/jobs/all${queryString ? `?${queryString}` : ""}`;
  };

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    const firstLoadTimeout = window.setTimeout(() => {
      controller.abort();
    }, WP_JOBS_FIRST_LOAD_TIMEOUT_MS);

    async function fetchLiveJobs() {
      const cachedJobs =
        liveJobsCache.data && Date.now() - liveJobsCache.timestamp < WP_JOBS_CACHE_TTL_MS
          ? liveJobsCache.data
          : null;

      if (cachedJobs) {
        setLiveJobs(cachedJobs);
        setJobsLoading(false);
      } else {
        setLiveJobs([]);
        setJobsLoading(true);
      }

      setJobsError("");
      try {
        const firstPage = await fetchJobsPage(
          { page: 1, perPage: WP_JOBS_FIRST_PAGE_SIZE },
          controller.signal
        );
        window.clearTimeout(firstLoadTimeout);
        const firstJobs = normalizeJobs(firstPage.posts);

        if (cancelled) return;

        const initialJobs = cachedJobs ? mergeJobs(cachedJobs, firstJobs) : firstJobs;
        setLiveJobs(initialJobs);
        setJobsLoading(false);
        cacheLiveJobs(initialJobs);

        const remainingCount = Math.max(0, firstPage.total - WP_JOBS_FIRST_PAGE_SIZE);
        const remainingPages = Math.ceil(remainingCount / WP_JOBS_BACKGROUND_PAGE_SIZE);
        if (!remainingPages) return;

        try {
          const remainingResponses = await Promise.all(
            Array.from({ length: remainingPages }, (_, index) =>
              fetchJobsPage(
                {
                  perPage: WP_JOBS_BACKGROUND_PAGE_SIZE,
                  offset: WP_JOBS_FIRST_PAGE_SIZE + index * WP_JOBS_BACKGROUND_PAGE_SIZE,
                },
                controller.signal
              )
            )
          );

          if (cancelled) return;

          const allJobs = mergeJobs(
            firstJobs,
            normalizeJobs(remainingResponses.flatMap((response) => response.posts))
          );
          setLiveJobs(allJobs);
          cacheLiveJobs(allJobs);
        } catch (backgroundError) {
          if (backgroundError?.name !== "AbortError") {
            cacheLiveJobs(firstJobs);
          }
        }
      } catch (error) {
        if (error?.name === "AbortError") return;
        if (!cancelled) {
          if (cachedJobs) {
            setLiveJobs(cachedJobs);
            setJobsError("");
          } else {
            setLiveJobs([]);
            setJobsError("Unable to load live jobs right now.");
          }
          setJobsLoading(false);
        }
      }
    }

    fetchLiveJobs();
    return () => {
      cancelled = true;
      window.clearTimeout(firstLoadTimeout);
      controller.abort();
    };
  }, []);

  const topPicks = useMemo(() => {
    return jobsSource.filter((j) => j.topPick).slice(0, 3);
  }, [jobsSource]);

  const forYou = useMemo(() => jobsSource.slice(0, 6), [jobsSource]);

  const recentlyAdded = useMemo(() => {
    const newestFirst = [...jobsSource].sort((a, b) => (a.postedDaysAgo ?? 9999) - (b.postedDaysAgo ?? 9999));
    return pickNWithFallback(newestFirst.slice(6, 12), newestFirst, 6);
  }, [jobsSource]);

  const featuredCompanies = useMemo(() => {
    const map = new Map();
    jobsSource.forEach((job) => {
      if (!job.company || map.has(job.company)) return;
      map.set(job.company, {
        id: `fc-${job.company}`,
        name: job.company,
        logo: job.logo || getLogoUrl(job.company),
        blurb: job.aboutCompany || job.summary || `${job.company} is actively hiring for ${job.category || "travel"} roles.`,
      });
    });
    return Array.from(map.values());
  }, [jobsSource]);

  const jobsByLocation = useMemo(() => {
    const locationImages = [
      "https://images.unsplash.com/photo-1582510003544-4d00b7f74220?auto=format&fit=crop&w=1400&q=60",
      "https://images.unsplash.com/photo-1592639296346-560c37a0f711?auto=format&fit=crop&w=1400&q=60",
      "https://images.unsplash.com/photo-1534237710431-e2fc698436d0?auto=format&fit=crop&w=1400&q=60",
      "https://images.unsplash.com/photo-1527838832700-5059252407fa?auto=format&fit=crop&w=1400&q=60",
      "https://images.unsplash.com/photo-1527631746610-bca00a040d60?auto=format&fit=crop&w=1400&q=60",
      "https://images.unsplash.com/photo-1571679654681-ba01b9e1e117?auto=format&fit=crop&w=1400&q=60",
    ];
    const counts = new Map();
    jobsSource.forEach((job) => {
      const name = (job.location || "India").split(",")[0].trim();
      if (!name) return;
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6)
      .map(([name, openJobs], index) => ({
        id: `loc-${name}`,
        name,
        openJobs,
        image: locationImages[index % locationImages.length],
      }));
  }, [jobsSource]);

  const jobTitleTags = useMemo(() => ([
    { label: "Customer Service", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/chat-bubble-left-right.svg", iconBg: "#eef2ff" },
    { label: "Hospitality & Tourism", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/building-office-2.svg", iconBg: "#fef3c7" },
    { label: "Human Resource", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/user-group.svg", iconBg: "#dcfce7" },
    { label: "Legal", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/scale.svg", iconBg: "#f3e8ff" },
    { label: "Marketing", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/megaphone.svg", iconBg: "#ffe4e6" },
    { label: "Operations", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/cog-6-tooth.svg", iconBg: "#e0f2fe" },
    { label: "Product Development", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/cube-transparent.svg", iconBg: "#ccfbf1" },
    { label: "Technology", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/cpu-chip.svg", iconBg: "#dbeafe" },
    { label: "Administration", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/clipboard-document-list.svg", iconBg: "#ede9fe" },
    { label: "Assistant Chief Engineer", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/wrench-screwdriver.svg", iconBg: "#ffedd5" },
    { label: "Sales", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/presentation-chart-line.svg", iconBg: "#ecfccb" },
    { label: "Finance", icon: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/banknotes.svg", iconBg: "#dcfce7" },
  ]), []);

  const toggleBookmark = (jobId) => {
    setBookmarks((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  if (routeLocation.pathname === "/jobs/all" || routeLocation.pathname === "/jobs/all/") {
    return (
      <CompaniesPage
        darkMode={isDark}
        jobs={listingJobs}
        title={listingCategory || (hasListingFilters ? "Filtered jobs" : "Your next move")}
        subtitle=""
        loading={jobsLoading}
        error={jobsError}
      />
    );
  }

  if (routeLocation.pathname === "/jobs/companies" || routeLocation.pathname === "/jobs/companies/") {
    return <CompaniesPage darkMode={isDark} jobs={jobsSource} title="Top companies hiring now" loading={jobsLoading} error={jobsError} />;
  }

  if (routeLocation.pathname.startsWith("/jobs/company/")) {
    const companyName = routeLocation.pathname.replace("/jobs/company/", "").replace(/\/$/, "");
    return <CompanyLanding companyName={companyName} isDark={isDark} jobsSource={jobsSource} loading={jobsLoading} onBack={() => navigate("/jobs")} />;
  }

  const careerBannerUrl = `${import.meta.env.BASE_URL}assets/images/jobs/career.avif`;

  const typeIcons = {
    All: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/squares-2x2.svg",
    "Full-time": "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/briefcase.svg",
    "Part-time": "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/clock.svg",
    Contract: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/document-text.svg",
    Internship: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/academic-cap.svg",
  };

  const modeIcons = {
    Remote: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/home.svg",
    Hybrid: "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/arrows-right-left.svg",
    "On-site": "https://cdn.jsdelivr.net/npm/heroicons@2.1.5/24/outline/building-office.svg",
  };

  return (
    /*
     * FIX 3: Sticky nav fix — replace `overflow-x-hidden` with `overflow: clip`
     * via inline style. `overflow: clip` prevents horizontal scroll bleed but
     * does NOT create a new containing block, so `position: sticky` on ancestor
     * nav elements still works correctly.
     */
    <div
      className={classNames("w-full font-poppins", isDark ? "bg-black" : "bg-white")}
      style={{ overflowX: "clip" }}
    >
      <style>{NoScrollbarCss}</style>
      <div className="mx-auto w-full max-w-[1500px] px-4">

        {/*
         * FIX 2: Banner overflow fix — use `-mx-4` to bleed to the outer container
         * edge instead of `w-screen -ml-[50vw] -mr-[50vw]` which breaks inside a
         * padded wrapper. The parent `px-4` is countered by `-mx-4` here, and the
         * background covers the full bleed width correctly.
         */}
        <section className="relative left-1/2 w-screen -translate-x-1/2 overflow-hidden px-4 pb-[4.6rem] pt-[4.6rem] sm:px-10">
          <div
            aria-hidden="true"
            className="absolute inset-0 bg-cover bg-top"
            style={{ backgroundImage: `url('${careerBannerUrl}')` }}
          />
          <div
            aria-hidden="true"
            className={classNames("absolute inset-0", isDark ? "bg-black/70" : "bg-black/55")}
          />
          <div className="relative mx-auto flex max-w-[1500px] flex-col items-center text-center">
            <img
              src="http://localhost:5173/wp-content/uploads/LiveBackUp2026/src/legacy/assets/travel-logo-dark-ektEo9yz.png"
              alt="Travel logo"
              width={250}
              height={125}
              className="mx-auto mb-6"
              loading="lazy"
              decoding="async"
            />
            <h1
              className={classNames(
                "text-[34px] leading-[1.05] sm:text-[44px] font-poppins",
                "text-white"
              )}
            >
              Find your dream job now
            </h1>

            {/* Search bar */}
            <div className="mt-10 w-full">
              <div className="mx-auto flex w-full max-w-[980px] flex-col overflow-hidden rounded-full bg-white sm:flex-row">
                <div className="flex flex-1 items-center gap-3 px-5 py-4">
                  <i className="fa-solid fa-magnifying-glass text-black/60" />
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Enter skills / designations / companies"
                    className="w-full bg-transparent text-[14px] outline-none text-black placeholder:text-black/45"
                  />
                </div>

                <div className="hidden w-px bg-black/10 sm:block" />

                <div className="flex items-center gap-3 px-5 py-4 sm:w-[240px]">
                  <i className="fa-solid fa-briefcase text-black/60" />
                  <select
                    value={experience}
                    onChange={(e) => setExperience(e.target.value)}
                    className="w-full cursor-pointer bg-transparent text-[14px] outline-none text-black"
                  >
                    <option value="0-2 years">Select experience</option>
                    <option value="0-2 years">0–2 years</option>
                    <option value="3-5 years">3–5 years</option>
                    <option value="6-9 years">6–9 years</option>
                    <option value="10+ years">10+ years</option>
                  </select>
                </div>

                <div className="hidden w-px bg-black/10 sm:block" />

                <div className="flex items-center gap-3 px-5 py-4 sm:w-[240px]">
                  <i className="fa-solid fa-location-dot text-black/60" />
                  <input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="Enter location"
                    className="w-full bg-transparent text-[14px] outline-none text-black placeholder:text-black/45"
                  />
                </div>

                <button
                  type="button"
                  onClick={() => navigate(buildJobsListingPath())}
                  className="m-2 inline-flex h-12 items-center justify-center rounded-full bg-[#d02525] px-8 text-[14px] font-semibold text-white"
                >
                  Search <NorthEastArrow />
                </button>
	              </div>
	            </div>
	            <div className="mx-auto mt-[4.6rem] flex w-full max-w-[980px] flex-wrap justify-center gap-2">
                  {jobTitleTags.map((t) => (
                    <JobTitleChip
                      key={t.label}
                      active={query === t.label}
                      label={t.label}
                      icon={t.icon}
                      iconBg={t.iconBg}
                      isDark={isDark}
                      onClick={() => {
                        setQuery(t.label);
                        setExperience("0-2 years");
                        setLocation("");
                        setJobType("All");
                        setMode("");
                        navigate(`/jobs/all?category=${toJobCategorySlug(t.label)}`);
                      }}
                    />
                  ))}
	            </div>
	          </div>
	        </section>

	        {/* ── Filters ── */}
	        <div className="rounded-2xl mt-12">
	          {/* Type + Mode pills */}
	          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              <span className={classNames("mr-2 text-[14px] font-semibold", isDark ? "text-white/75" : "text-black/70")}>
                Type:
              </span>
              {JOB_TYPES.map((t) => (
                <Pill
                  key={t}
                  active={jobType === t}
                  icon={typeIcons[t]}
                  isDark={isDark}
                  onClick={() => {
                    setJobType(t);
                    navigate(buildJobsListingPath({ type: t === "All" ? "" : t }));
                  }}
                >
                  {t}
                </Pill>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className={classNames("mr-2 text-[14px] font-semibold", isDark ? "text-white/75" : "text-black/70")}>
                Mode:
              </span>
              {JOB_MODES.map((m) => (
                <Pill
                  key={m}
                  active={mode === m}
                  icon={modeIcons[m]}
                  isDark={isDark}
                  onClick={() => {
                    setMode(m);
                    navigate(buildJobsListingPath({ mode: m }));
                  }}
                >
                  {m}
                </Pill>
              ))}
            </div>
          </div>
        </div>

        {jobsLoading || jobsError ? (
          <div className={classNames(
            "mt-8 rounded-2xl px-5 py-4 text-center text-[15px] font-medium",
            isDark ? "bg-white/10 text-white" : "bg-[#f8f9fa] text-black"
          )}>
            {jobsLoading
              ? "Loading live jobs from Travel RethinkWays..."
              : `Unable to load live jobs: ${jobsError}`}
          </div>
        ) : null}

        {/* ── For You ── */}
        <section className="mt-12 font-poppins">
          <div className={classNames(
            "mb-6 flex items-center justify-between gap-4 rounded-sm px-0 py-0",
            isDark ? "text-white" : "text-black"
          )}>
            <div>
              <p className="m-0 font-poppins text-[50px] font-medium leading-[1.05] text-[#000]">
                Your next move
              </p>
              <h2 className={classNames("m-0 mt-[15px] font-poppins text-[18px] font-thin leading-[1.1]", isDark ? "text-white" : "text-black")}>
                Roles that match where you are, and where you're headed
              </h2>
            </div>
            <button
              type="button"
              onClick={() => navigate("/jobs/all")}
              className={classNames(
                "inline-flex h-11 shrink-0 items-center justify-center rounded-full border px-5 text-[14px] font-semibold",
                isDark ? "border-white/30 text-white hover:bg-white/10" : "border-black/30 text-black hover:bg-black/5"
              )}
            >
              View all <NorthEastArrow />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
            {forYou.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                bookmarked={bookmarks.has(job.id)}
                isDark={isDark}
                layout="split"
                variant="recent"
                recentLogoSize={240}
                recentStacked
                hiddenSkills={["Client relationship management"]}
                onToggleBookmark={() => toggleBookmark(job.id)}
              />
            ))}
          </div>
        </section>

        {/* ── Featured companies actively hiring ── */}
        <section className="mt-12 font-poppins">
          <div className={classNames(
            "mb-6 flex items-center justify-between gap-4 rounded-sm px-0 py-0",
            isDark ? "text-white" : "text-black"
          )}>
            <div>
              <p className="m-0 font-poppins text-[50px] font-medium leading-[1.05] text-[#000]">
                Brands moving fast
              </p>
              <h2 className={classNames("m-0 mt-[15px] font-poppins text-[18px] font-thin leading-[1.1]", isDark ? "text-white" : "text-black")}>
                Vetted companies across hospitality, aviation, cruises and more
              </h2>
            </div>
          </div>

          <div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {featuredCompanies.slice(0, visibleFeaturedCompanies).map((c) => (
                <FeaturedCompanyCard
                  key={c.id}
                  company={{ ...c, onViewJobs: () => navigate(`/jobs/company/${encodeURIComponent(c.name)}`) }}
                  isDark={isDark}
                />
              ))}
            </div>
            {visibleFeaturedCompanies < featuredCompanies.length ? (
              <div className="mt-6 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleFeaturedCompanies((count) => Math.min(count + 12, featuredCompanies.length))}
                  className={classNames(
                    "inline-flex h-11 items-center justify-center rounded-full border px-6 text-[14px] font-semibold",
                    isDark ? "border-white/30 text-white hover:bg-white/10" : "border-black/30 text-black hover:bg-black/5"
                  )}
                >
                  Load more <NorthEastArrow />
                </button>
              </div>
            ) : null}
          </div>

        </section>

        {/* ── Recently Added ── */}
        <section className="mt-12 font-poppins">
          <div className={classNames(
            "mb-6 flex items-center justify-between gap-4 rounded-sm px-0 py-0",
            isDark ? "text-white" : "text-black"
          )}>
            <div>
              <p className="m-0 font-poppins text-[50px] font-medium leading-[1.05] text-[#000]">
                New on the map
              </p>
              <h2 className={classNames("m-0 mt-[15px] font-poppins text-[18px] font-thin leading-[1.1]", isDark ? "text-white" : "text-black")}>
                Brand new roles from companies actively expanding their teams
              </h2>
            </div>
            <button
              type="button"
              onClick={() => navigate("/jobs")}
              className={classNames(
                "inline-flex h-11 shrink-0 items-center justify-center rounded-full border px-5 text-[14px] font-semibold",
                isDark ? "border-white/30 text-white hover:bg-white/10" : "border-black/30 text-black hover:bg-black/5"
              )}
            >
              View all <NorthEastArrow />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {recentlyAdded.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                bookmarked={bookmarks.has(job.id)}
                isDark={isDark}
                layout="split"
                variant="recent"
                recentLogoSize={175}
                actionsOnCard
                hiddenSkills={["Client relationship management"]}
                onToggleBookmark={() => toggleBookmark(job.id)}
              />
            ))}
          </div>
        </section>

        {/* ── Jobs by Location ── */}
        <section className="mt-16 pb-8 font-poppins">
          <div className={classNames(
            "mb-6 flex items-center justify-between gap-4 rounded-sm px-0 py-0",
            isDark ? "text-white" : "text-black"
          )}>
            <div>
              <p className="m-0 font-poppins text-[50px] font-medium leading-[1.05] text-[#000]">
                Work where you love
              </p>
              <h2 className={classNames("m-0 mt-[15px] font-poppins text-[18px] font-thin leading-[1.1]", isDark ? "text-white" : "text-black")}>
                Filter by destination and find roles in the cities that suit your life
              </h2>
            </div>
            <button
              type="button"
              onClick={() => navigate("/jobs")}
              className={classNames(
                "inline-flex h-11 shrink-0 items-center justify-center rounded-full border px-5 text-[14px] font-semibold",
                isDark ? "border-white/30 text-white hover:bg-white/10" : "border-black/30 text-black hover:bg-black/5"
              )}
            >
              View all <NorthEastArrow />
            </button>
          </div>

          <div className="mx-auto grid max-w-[1500px] grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {jobsByLocation.map((loc) => (
              <LocationCard key={loc.id} item={loc} isDark={isDark} />
            ))}
          </div>
        </section>

      </div>
    </div>
  );
}
